import i18n from "i18n";
import _ from "lodash";
import moment from "moment";
import { isValidPhoneNumber } from "react-phone-number-input";
import DashboardSimulazioneFormModel from "stores/simulazione/models/DashboardSimulazioneFormModel";
import RichiestaCedibileModel from "stores/simulazione/models/RichiestaCedibileModel";
import SimulazioneStepOneFormModel from "stores/simulazione/models/SimulazioneStepOneFormModel";
import SimulazioneStepTwoFormModel from "stores/simulazione/models/SimulazioneStepTwoFormModel";
import AccessoViewModel from "stores/utente/models/AccessoViewModel";
import CambioPasswordViewModel from "stores/utente/models/CambioPasswordViewModel";
import RecuperoPasswordViewModel from "stores/utente/models/RecuperoPasswordViewModel";
import * as Yup from "yup";
import { ObjectSchema, ValidationError } from "yup";
import FormatUtility, { ClonedObject } from "./FormatUtility";
import SimulazioneStepThreeFormModel from "stores/simulazione/models/SimulazioneStepThreeFormModel";
import ProvvigioniComissioniFormModel from "stores/simulazione/models/ProvvigioniComissioniFormModel";
import TanBaseFormModel from "stores/simulazione/models/TanBaseFormModel";
import OneriFormModel from "stores/simulazione/models/OneriFormModel";
import SpeseIstruttoriaFormModel from "stores/simulazione/models/SpeseIstruttoriaFormModel";
import SimulazioneCreditoNetFormModel from "stores/simulazione/models/SimulazioneCreditoNetFormModel";
import NuovoAppuntamentoFormModel from "stores/agenda/models/NuovoAppuntamentoFormModel";
import NuovoMessaggioFormModel from "stores/messaggio/models/NuovoMessaggioFormModel";
import NuovoNotaFormModel from "stores/note/models/NuovoNotaFormModel";
import CercaPreventiviFormModel from "stores/ricerca/models/CercaPreventiviFormModel";
import CercaSimulazioneFormModel from "stores/ricerca/models/CercaSimulazioneFormModel";
import CommissioniBancaSegnalanteFormModel from "stores/simulazione/models/CommissioniBancaSegnalanteFormModel";
import ValoriModel from "stores/simulazione/models/ValoriModel";

export type ValidationResult = {
    message: string;
    invalid: boolean;
};

export type ValidationContainer<Object> = ClonedObject<Object, ValidationResult>;

export type ValidationSchema<Object extends object> = ObjectSchema<RecursivePartial<Object>>;

export const mailRegex = /^([A-Za-z0-9_\-.]){1,}@([A-Za-z0-9_\-.]){1,}\.([A-Za-z]){2,4}$/;

export const codiceFiscaleRegex = /^[a-zA-Z]{6}[0-9]{2}[a-zA-Z][0-9]{2}[a-zA-Z][0-9]{3}[a-zA-Z]$/;

//export const phoneRegex = /^\+|[0]{1}\d{10,15}_{0,5}$/;

export const phoneRegex = /^([+]?[\s0-9]+)?(\d{3}|[(]?[0-9]+[)])?([-]?[\s]?[0-9])+$/i;

export default abstract class ValidationUtility {
    static parent: any;
    private constructor() {}

    private static setLocale() {
        Yup.setLocale({
            mixed: {
                default: i18n.t("error_global_default"),
                required: i18n.t("error_global_required"),
            },
            string: {
                min: i18n.t("error_string_min"),
                max: i18n.t("error_string_max"),
            },
            number: {
                min: i18n.t("error_number_min"),
                max: i18n.t("error_number_max"),
                integer: i18n.t("error_number_integer"),
            },
            date: {
                min: i18n.t("error_date_min"),
                max: i18n.t("error_date_max"),
            },
        });
    }

    public static setValidationContainer<T>(viewModel: T): ValidationContainer<T> {
        let propContent = { message: "", invalid: false } as ValidationResult;
        let replaceValuesFunction = () => {
            return propContent;
        };
        return FormatUtility.cloneObject<T, ValidationResult>(
            viewModel,
            replaceValuesFunction
        ) as ValidationContainer<T>;
    }

    private static resetValidationContainer<T>(errors: ValidationContainer<T>): ValidationContainer<T> {
        // reset all errors
        let replaceValuesFunction = (val: any) => {
            switch (val.constructor.name) {
                case "String":
                    return ""; //message
                case "Boolean":
                    return false; //invalid
            }
            return val;
        };
        return FormatUtility.cloneObject<ValidationContainer<T>, ValidationResult>(
            errors,
            replaceValuesFunction
        ) as ValidationContainer<T>;
    }

    public static checkValidity<T>(errors: ValidationContainer<T>) {
        let result: boolean[] = [];
        FormatUtility.getNestedFieldsValues(errors, "invalid", result);
        if (result.some((x) => x === true)) {
            return false;
        } else {
            return true;
        }
    }

    public static scrollToFirstError<T>(container: ValidationContainer<T>, idPrefix?: string) {
        const errors = Object.entries(container)
            .filter((field: [string, unknown]) => {
                const value = field[1] as ValidationResult;
                return value && value.invalid;
            })
            .map((error) => error[0]);

        if (errors && errors.length > 0) {
            const positions = errors.reduce((positions, error) => {
                const field = document.getElementById((idPrefix ?? "") + error);
                if (field) {
                    positions.push(field.getBoundingClientRect().top);
                } else {
                    console.error("scrollToFirstError field not found in DOM: ", error);
                }
                return positions;
            }, [] as number[]);

            if (positions && positions.length > 0) {
                const firstPos = Math.min.apply(Math, positions);
                ValidationUtility.scrollToPosition(firstPos);

                const firstField = document.getElementById((idPrefix ?? "") + errors[0]);
                if (firstField) firstField.focus();
            }
        }
    }

    public static scrollToPosition(position: number) {
        if (position < 0) {
            position = 0;
        }
        const container = document.getElementById("root");
        if (container) {
            container.scrollTo({ top: position });
        } else {
            window.scrollTo({ top: position });
        }
    }

    public static async validateSchema<T extends object>(
        schema: ValidationSchema<T>,
        errors: ValidationContainer<T>,
        viewModel: T,
        fieldName?: string,
        fieldValue?: any,
        params?: object
    ) {
        // check if schema contains field
        if (fieldName !== undefined) {
            let field = _.get(schema.fields, fieldName.replace(".", ".fields.").replace(/\[(.*?)\]/g, ".innerType"));
            if (field === undefined) {
                return errors;
            }
        }

        // if value passed, update viewModel
        if (fieldName !== undefined && fieldValue !== undefined) {
            _.set(viewModel, fieldName, fieldValue);
        }

        // replace empty strings with undefined
        let replaceValuesFunction = (val: any) => {
            if (typeof val === "string" && val === "") {
                return undefined;
            }
            return val;
        };
        viewModel = FormatUtility.cloneObject<T, any>(viewModel, replaceValuesFunction) as T;

        // validate schema (get all errors)
        await schema
            .validate(viewModel, { abortEarly: false, context: params })
            .then(() => {
                // reset all errors
                errors = ValidationUtility.resetValidationContainer(errors);
            })
            .catch((err: ValidationError) => {
                // set errors
                if (fieldName !== undefined) {
                    // set current field error
                    let fieldErrors = err.inner.filter((x) => x.path === fieldName);
                    let fieldMessage = fieldErrors.length > 0 ? fieldErrors[0].message : "";
                    let result = {
                        message: fieldMessage,
                        invalid: fieldMessage !== "",
                    } as ValidationResult;
                    _.set(errors, fieldName, result);
                } else {
                    // reset all errors
                    errors = ValidationUtility.resetValidationContainer(errors);

                    // set all fields errors
                    err.inner.forEach((error) => {
                        let fieldName = error.path;
                        let fieldMessage = error.message;
                        // ignore if an error is already setted
                        let current = _.get(errors, fieldName);
                        if (current) {
                            if (current.invalid) {
                                return;
                            }
                        }
                        let result = {
                            message: fieldMessage,
                            invalid: fieldMessage !== "",
                        } as ValidationResult;
                        _.set(errors, fieldName, result);
                    });
                }
            });
        // return new errors object
        return errors;
    }

    // #region dismessi il 04/06/2020
    private static getFieldName(target: EventTarget & HTMLFormElement) {
        let fieldName =
            target.name ||
            target.id ||
            (target.parentElement?.parentElement?.nextElementSibling as HTMLInputElement).name ||
            (target.parentElement?.parentElement?.nextElementSibling as HTMLInputElement).id ||
            "";
        return fieldName;
    }

    private static async validateField<T extends object>(
        schema: ValidationSchema<T>,
        errors: ValidationContainer<T>,
        name: string,
        value: string,
        viewModel: T
    ) {
        let result = {
            message: "",
            invalid: false,
        } as ValidationResult;
        let field = _.get(schema.fields, name.replace(".", ".fields."));
        if (field !== undefined) {
            await field
                .validate(value === "" ? undefined : value, { context: viewModel })
                .then(() => {
                    result.message = "";
                    result.invalid = false;
                })
                .catch((err: any) => {
                    result.message = err.message;
                    result.invalid = true;
                });
            _.set(errors, name, result);
        }
        return errors;
    }

    private static async validateSchemaIteration<T extends object>(
        schema: ValidationSchema<T>,
        errors: ValidationContainer<T>,
        viewModel: T,
        obj: ValidationSchema<T> = schema,
        path: string = "",
        fieldContainer: string = "fields"
    ): Promise<ValidationContainer<T>> {
        if (fieldContainer in obj) {
            errors = await ValidationUtility.validateSchemaIteration(schema, errors, viewModel, obj[fieldContainer]);
        } else {
            let fieldsNames = Object.getOwnPropertyNames(obj);
            for (let i = 0; i < fieldsNames.length; i++) {
                let fieldName = fieldsNames[i];
                if (fieldContainer in obj[fieldName]) {
                    let newPath = "";
                    if (path.length > 0) {
                        newPath += path + ".";
                    }
                    newPath += fieldName;
                    errors = await ValidationUtility.validateSchemaIteration(
                        schema,
                        errors,
                        viewModel,
                        obj[fieldName][fieldContainer],
                        newPath
                    );
                } else {
                    let newFieldName = "";
                    if (path.length > 0) {
                        newFieldName += path + ".";
                    }
                    newFieldName += fieldName;
                    let value = _.get(viewModel, newFieldName);
                    if (value !== undefined) {
                        errors = await ValidationUtility.validateField(schema, errors, newFieldName, value, viewModel);
                    }
                }
            }
        }
        return errors;
    }

    private static async validateForm<T extends object>(
        schema: ValidationSchema<T>,
        errors: ValidationContainer<T>,
        viewModel: T
    ) {
        return await ValidationUtility.validateSchemaIteration<T>(schema, errors, viewModel);
    }
    // #endregion dismessi il 04/06/2020

    public static getSchemaAccesso(): ValidationSchema<AccessoViewModel> {
        ValidationUtility.setLocale();
        return Yup.object().shape<Partial<AccessoViewModel>>({
            username: Yup.string()
                .required("Il campo Username è obbligatorio")
                .email("Formato email non valido")
                .label("Username"),
            password: Yup.string().required("Il campo Password è obbligatorio").label("Password"),
        });
    }

    public static getSchemaRecuperoPassword(): ValidationSchema<RecuperoPasswordViewModel> {
        ValidationUtility.setLocale();
        return Yup.object().shape<Partial<RecuperoPasswordViewModel>>({
            username: Yup.string()
                .required("Il campo Username è obbligatorio")
                .email("Formato email non valido")
                .label("Username"),
        });
    }

    public static getSchemaCambioPassword(): ValidationSchema<CambioPasswordViewModel> {
        ValidationUtility.setLocale();
        return Yup.object().shape<Partial<CambioPasswordViewModel>>({
            username: Yup.string()
                .required("Il campo Username è obbligatorio")
                .email("Formato email non valido")
                .label("Username"),
            password: Yup.string()
                .required("Il campo Password è obbligatorio")
                .test(
                    "format",
                    "La password deve contenere almeno una lettera maiuscola, almeno un carattere numerico e almeno un carattere speciale",
                    (val) => {
                        return this.checkIfPasswordIsValid(val);
                    }
                )
                //.oneOf([Yup.ref("repeatPassword"), null], i18n.t("confermapage-err-regconferma"))
                .label("Password"),
            confermaPassword: Yup.string()
                .required("Il campo Conferma Password è obbligatorio")
                .test(
                    "format",
                    "La password deve contenere almeno una lettera maiuscola, almeno un carattere numerico e almeno un carattere speciale",
                    (val) => {
                        return this.checkIfPasswordIsValid(val);
                    }
                )
                .oneOf([Yup.ref("password"), null], "Le due password devono coincidere")
                .label("Conferma password"),
        });
    }

    private static checkIfNotContainsNumbers = (value: string) => {
        const regExpress = new RegExp(/[^0-9]/g);
        return regExpress.test(value);
    };

    private static checkIfContainsEuropeanChar = (value: string) => {
        const regExpress = new RegExp(
            /^[a-zA-ZàáâäãåąčćęèéêëėįìíîïłńòóôöõøùúûüųūÿýżźñçčšžÀÁÂÄÃÅĄĆČĖĘÈÉÊËÌÍÎÏĮŁŃÒÓÔÖÕØÙÚÛÜŲŪŸÝŻŹÑßÇŒÆČŠŽ∂ð ,.'-]+$/u
        );
        return regExpress.test(value);
    };

    private static checkIfContainsSpecialChar = (value: string) => {
        const regExpress = new RegExp(
            /^[a-zA-Z0-9àáâäãåąčćęèéêëėįìíîïłńòóôöõøùúûüųūÿýżźñçčšžÀÁÂÄÃÅĄĆČĖĘÈÉÊËÌÍÎÏĮŁŃÒÓÔÖÕØÙÚÛÜŲŪŸÝŻŹÑßÇŒÆČŠŽ∂ð '-]+$/u
        );
        return regExpress.test(value);
    };

    private static checkIfNumber = (value: string) => {
        const regExpress = new RegExp(/^[0-9]*$/g);
        return regExpress.test(value);
    };

    private static checkIfPasswordIsValid = (password: string) => {
        const regExpress = new RegExp(/^(?=.{8,}$)(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[\W_]).*$/);
        return regExpress.test(password);
    };

    private static checkIfCodiceConvenzioneIsValid = (codiceConvenzione: string) => {
        const regExpress = new RegExp("^[a-zA-Z0-9]*$");
        return !codiceConvenzione || regExpress.test(codiceConvenzione);
    };

    private static checkIfImportoIsValid = (value: number | string): boolean => {
        if (value != null) {
            let number = NaN;
            switch (typeof value) {
                case "number":
                    number = value;
                    break;
                case "string":
                    number = parseFloat(
                        value
                            .toString()
                            .replace(/[^0-9,]/g, "")
                            .replace(",", ".")
                    );
                    break;
            }
            return isNaN(number) === false;
        }
        return false;
    };

    private static checkMinNumber = (value: number, minValue: number, exclude: boolean = false) => {
        if (value != null) {
            if (exclude) {
                return value > minValue;
            } else {
                return value >= minValue;
            }
        }
        return false;
    };

    private static checkMaxNumber = (value: number, maxValue: number, exclude: boolean = false) => {
        if (value != null) {
            if (exclude) {
                return value < maxValue;
            } else {
                return value <= maxValue;
            }
        }
        return false;
    };

    private static checkIfDateIsValidOrUndefined = (date: Date | moment.Moment | string): boolean => {
        if (date === undefined) {
            return true;
        }
        let dateValidated = FormatUtility.dateOrUndefined(date);
        if (dateValidated) {
            return true;
        }
        return false;
    };

    private static checkMinDate = (
        date: Date | moment.Moment | string,
        minDate: Date | moment.Moment | string,
        exclude: boolean = false
    ): boolean => {
        if (date === undefined) {
            return true;
        }
        let dateValidated = FormatUtility.dateOrUndefined(date);
        let minDateValidated = FormatUtility.dateOrUndefined(minDate);
        if (dateValidated && minDateValidated) {
            if (exclude) {
                return dateValidated > minDateValidated;
            } else {
                return dateValidated >= minDateValidated;
            }
        }
        return false;
    };

    private static checkMinTime = (
        ora: Date | moment.Moment | string,
        minTime: Date | moment.Moment | string,
        exclude: boolean = false
    ): boolean => {
        if (ora === undefined) {
            return true;
        }

        if (ora !== null && minTime !== null && ora !== undefined && minTime !== undefined) {
            if (ora && minTime) {
                if (exclude) {
                    return ora > minTime;
                } else {
                    return ora >= minTime;
                }
            }
        }
        return false;
    };

    private static checkMaxDate = (
        date: Date | moment.Moment | string,
        maxDate: Date | moment.Moment | string,
        exclude: boolean = false
    ): boolean => {
        if (date === undefined) {
            return true;
        }
        let dateValidated = FormatUtility.dateOrUndefined(date);
        let maxDateValidated = FormatUtility.dateOrUndefined(maxDate);
        if (dateValidated && maxDateValidated) {
            if (exclude) {
                return dateValidated < maxDateValidated;
            } else {
                return dateValidated <= maxDateValidated;
            }
        }
        return false;
    };

    private static checkMaxTime = (
        oraInizio: Date | moment.Moment | string,
        oraFine: Date | moment.Moment | string,
        exclude: boolean = false
    ): boolean => {
        if (oraInizio === undefined) {
            return true;
        }

        if (oraInizio !== null && oraFine !== null && oraInizio !== undefined && oraFine !== undefined) {
            if (oraInizio && oraFine) {
                if (exclude) {
                    return oraInizio < oraFine;
                } else {
                    return oraInizio <= oraFine;
                }
            }
        }
        return false;
    };

    public static preventEnterKeySubmit = (event: React.KeyboardEvent<HTMLFormElement>) => {
        if (event.key === "Enter" && event.shiftKey === false) {
            event.preventDefault();
            event.stopPropagation();
        }
    };

    public static checkPhoneNumber = (value: string) => {
        let result = isValidPhoneNumber(value);
        if (value && value.startsWith("+39") && value.startsWith("+393") === false) {
            // italian phone number but not mobile phone
            result = false;
        }
        return result;
    };

    public static checkCodiceFiscale = (
        cognome: string,
        nome: string,
        dataNascita: string,
        sesso: string,
        luogoNascita: string,
        codiceFiscale: string
    ): boolean => {
        if (codiceFiscale === undefined) {
            return true;
        }
        let sessoFormatted: "M" | "F";
        switch (sesso) {
            case "M":
                sessoFormatted = "M";
                break;
            case "F":
                sessoFormatted = "F";
                break;
            default:
                return false;
        }
        let dateFormatted: Date | undefined = FormatUtility.dateOrUndefined(dataNascita);
        if (dateFormatted === undefined) {
            return false;
        }
        let codiceFiscaleCalcolato = FormatUtility.getCodiceFiscale(
            cognome,
            nome,
            dateFormatted,
            sessoFormatted,
            luogoNascita
        );
        codiceFiscale = codiceFiscale.toUpperCase();
        if (codiceFiscale === codiceFiscaleCalcolato) {
            return true;
        } else if (codiceFiscale.length === 16 && codiceFiscaleCalcolato.length === 16) {
            // controllo per accettare casi di omocodia
            const chPosizioniOmocodia = [14, 13, 12, 10, 9, 7, 6];
            const chValoriOmocodia = "LMNPQRSTUV";
            chPosizioniOmocodia.forEach((chPos: number) => {
                // se il carattere nella posizione corrente del codice fiscale
                // è il carattere applicato in caso di omocodia
                // ignora la posizione nel confronto
                const charCorrente = codiceFiscale.charAt(chPos);
                const charDefault = codiceFiscaleCalcolato.charAt(chPos);
                const numero = parseInt(charDefault);
                if (isNaN(numero) === false) {
                    const charDefaultReplaced = chValoriOmocodia.charAt(numero);
                    if (charCorrente === charDefaultReplaced) {
                        codiceFiscale = codiceFiscale.slice(0, chPos) + codiceFiscale.slice(chPos + 1);
                        codiceFiscaleCalcolato =
                            codiceFiscaleCalcolato.slice(0, chPos) + codiceFiscaleCalcolato.slice(chPos + 1);
                    }
                }
            });
            if (codiceFiscale === codiceFiscaleCalcolato) {
                return true;
            }
        }
        return false;
    };

    public static getCookie = (cookieName: string): string => {
        const name = cookieName + "=";
        const decodedCookie = decodeURIComponent(document.cookie);
        const ca = decodedCookie.split(";");
        for (let i = 0; i < ca.length; i++) {
            let c = ca[i];
            while (c.charAt(0) === " ") {
                c = c.substring(1);
            }
            if (c.indexOf(name) === 0) {
                return c.substring(name.length, c.length);
            }
        }
        return "";
    };

    private static checkRangeDataScadenza = (
        dataScadenza: string,
        dataRilascio: string,
        dataNascita: string
    ): boolean => {
        if (dataScadenza === undefined || dataRilascio === undefined || dataNascita === undefined) {
            return true;
        }

        let dataRilascioFormatted: Date | undefined = FormatUtility.dateOrUndefined(dataRilascio);
        let dataScadenzaFormatted: Date | undefined = FormatUtility.dateOrUndefined(dataScadenza);
        let dataNascitaFormatted: Date | undefined = FormatUtility.dateOrUndefined(dataNascita);
        if (
            dataRilascioFormatted === undefined ||
            dataScadenzaFormatted === undefined ||
            dataNascitaFormatted === undefined
        ) {
            return false;
        }
        let day = dataRilascioFormatted.getDate();
        let month = dataRilascioFormatted.getMonth();
        let year = dataRilascioFormatted.getFullYear();

        if (year > 2012) {
            let monthRilascio = dataRilascioFormatted.getMonth();
            // se è maggiore del 2012 bisogna aggiungere 10 anni alla data di rilascio e deve cadere il giorno di nascita
            month = dataNascitaFormatted.getMonth();
            day = dataNascitaFormatted.getDate();
            //se la data di rilascio è antecedente al giorno di nascita se invece è posteriore deve cadere nell'anno successivo
            if (monthRilascio >= month) {
                year += 1;
            }
        }
        let dataScadenzaCorretta = new Date(year + 10, month, day);
        if (dataScadenzaCorretta === dataScadenzaFormatted) {
            return true;
        } else {
            return false;
        }
    };

    public static getSchemaSimulazioneStepOne(): ValidationSchema<SimulazioneStepOneFormModel> {
        ValidationUtility.setLocale();
        return Yup.object().shape<Partial<SimulazioneStepOneFormModel>>({
            cliente: Yup.string().required("Selezionare la tipologia di utenza").label("Cliente"),
            canale: Yup.string().required("Selezionare il canale di interesse").label("Canale"),
            segmento: Yup.string().required("Selezionare il segmento di interesse").label("Canale"),
            // fonte: Yup.string().required("Selezionare la fonte di interesse"),
            // dettaglioFonte: Yup.string().required("	Selezionare il dettaglio fonte di interesse"),
            atc: Yup.string().label("ATC"),
            amministrazioneNonCensita: Yup.bool().required(),
            amministrazioneCensita: Yup.bool().required(),
            dataNascita: Yup.string()
                .required("Il campo 'Data di nascita' è obbligatorio")
                .test("format", "Formato data non valido", (val) => {
                    return this.checkIfDateIsValidOrUndefined(val);
                })
                .test("min", "E' necessario avere meno di 86 anni", (val) => {
                    let minDate = new Date();
                    minDate.setFullYear(minDate.getFullYear() - 86);
                    return this.checkMinDate(val, minDate);
                })
                .test("futura", "Data di nascita futura non ammessa", (val) => {
                    let maxDate = new Date();
                    return this.checkMaxDate(val, maxDate);
                })
                .test("minorenne", "E' necessario essere maggiorenni", (val) => {
                    let maxDate = new Date();
                    maxDate.setFullYear(maxDate.getFullYear() - 18);
                    return this.checkMaxDate(val, maxDate);
                })
                .label("Data nascita"),
            dataAssunzione: Yup.string()
                .when("$isTipologiaCQP", {
                    is: (isTipologiaCQP) => {
                        return isTipologiaCQP === undefined || isTipologiaCQP === false;
                    },
                    then: Yup.string()
                        .required("Inserire la data di assunzione")
                        .test("format", "Formato data non valido", (val) => {
                            return this.checkIfDateIsValidOrUndefined(val);
                        })
                        .test("min", "La data assunzione dev'essere successiva a 86 anni fa", (val) => {
                            let minDate = new Date();
                            minDate.setFullYear(minDate.getFullYear() - 86);
                            return this.checkMinDate(val, minDate);
                        })
                        .test("futura", "Data assunzione futura non ammessa", (val) => {
                            let maxDate = new Date();
                            return this.checkMaxDate(val, maxDate);
                        })
                        .label("Data assunzione"),
                })
                .label("Netto mensile"),
            // dataPensionamento: Yup.string().when("$isTipologiaCQP", {
            //     is: (isTipologiaCQP) => {
            //         return isTipologiaCQP === true;
            //     },
            //     then: Yup.string()
            //         .required("Inserire la data di pensionamento")
            //         .test("format", "Formato data non valido", (val) => {
            //             return this.checkIfDateIsValidOrUndefined(val);
            //         })
            //         .test({
            //             name: "max",
            //             exclusive: false,
            //             params: {},
            //             message: "La data di pensionamento dev'essere successiva alla data di nascita",
            //             test: function (value) {
            //                 const val = FormatUtility.dateInGivenFormat(value);
            //                 const dataNascita = FormatUtility.dateInGivenFormat(this.parent.dataNascita);
            //                 return val >= dataNascita;
            //             },
            //         })
            //         .label("Data pensionamento"),
            // }),
            nettoMensile: Yup.string()
                .when("$isTipologiaCQP", {
                    is: (isTipologiaCQP) => {
                        return isTipologiaCQP === undefined || isTipologiaCQP === false;
                    },
                    then: Yup.string()
                        .required("Indicare l'importo netto mensile	")
                        .notOneOf(["", "0", "0,00"], "Indicare l'importo dello stipendio netto mensile")
                        .test("format", "Il formato dell'importo dello stipendio netto mensile non è valido", (val) => {
                            return this.checkIfImportoIsValid(val);
                        })
                        .test(
                            "min",
                            "L'importo dello stipendio netto mensile deve essere maggiore di € 563,00",
                            (val) => {
                                let value = FormatUtility.numberFromNumberWithSeparators(val);
                                if (value != null) {
                                    return this.checkMinNumber(value, 563);
                                }
                                return false;
                            }
                        )
                        .test(
                            "max",
                            "L'importo dello stipendio netto mensile deve essere minore di € 100.000,00",
                            (val) => {
                                let value = FormatUtility.numberFromNumberWithSeparators(val);
                                if (value !== undefined) {
                                    return this.checkMaxNumber(value, 100000);
                                }
                                return false;
                            }
                        ),
                })
                .label("Netto mensile"),
            pensioneNettaMensile: Yup.string()
                .when("$isTipologiaCQP", {
                    is: (isTipologiaCQP) => {
                        return isTipologiaCQP === true;
                    },
                    then: Yup.string()
                        .required("Indicare l'importo pensione netta mensile")
                        .notOneOf(["", "0", "0,00"], "Indicare l'importo dello pensione")
                        .test("format", "Il formato dell'importo dello pensione non è valido", (val) => {
                            return this.checkIfImportoIsValid(val);
                        })
                        .test(
                            "min",
                            "L'importo della pensione netta mensile deve essere maggiore di € 563,00",
                            (val) => {
                                let value = FormatUtility.numberFromNumberWithSeparators(val);
                                if (value != null) {
                                    return this.checkMinNumber(value, 563);
                                }
                                return false;
                            }
                        )
                        .test(
                            "max",
                            "L'importo della pensione netta mensile deve essere minore di € 100.000,00",
                            (val) => {
                                let value = FormatUtility.numberFromNumberWithSeparators(val);
                                if (value !== undefined) {
                                    return this.checkMaxNumber(value, 100000);
                                }
                                return false;
                            }
                        ),
                })
                .label("Pensione netta mensile"),
            boxOneRata: Yup.string()
                .when("$rataDurataCollapse", {
                    is: (rataDurataCollapse) => {
                        return rataDurataCollapse === true;
                    },
                    then: Yup.string()
                        .when("$isTipologiaCQP", {
                            is: (isTipologiaCQP) => {
                                return isTipologiaCQP === true;
                            },
                            then: Yup.string().test({
                                name: "max",
                                exclusive: false,
                                params: {},
                                message: "La rata non può essere superiore ad un quinto della pensione netta mensile",
                                test: function (value) {
                                    let val = FormatUtility.numberFromNumberWithSeparators(value) || 0;
                                    let pensione =
                                        FormatUtility.numberFromNumberWithSeparators(
                                            this.parent.pensioneNettaMensile
                                        ) || 0;
                                    return val <= pensione / 5;
                                },
                            }),
                        })
                        .when("$isTipologiaCQP", {
                            is: (isTipologiaCQP) => {
                                return isTipologiaCQP === undefined || isTipologiaCQP === false;
                            },
                            then: Yup.string().test({
                                name: "max",
                                exclusive: false,
                                params: {},
                                message: "La rata non può essere superiore ad un quinto del netto mensile",
                                test: function (value) {
                                    let val = FormatUtility.numberFromNumberWithSeparators(value) || 0;
                                    let netto =
                                        FormatUtility.numberFromNumberWithSeparators(this.parent.nettoMensile) || 0;
                                    return val <= netto / 5;
                                },
                            }),
                        })
                        .required("Indicare la rata desiderata")
                        .notOneOf(["", "0", "0,00"], "Indicare la rata desiderata")
                        .test("format", "Il formato dell'importo rata non è valido", (val) => {
                            return this.checkIfImportoIsValid(val);
                        })
                        .test("min", "La rata richiesta deve essere maggiore di € 30,00", (val) => {
                            let value = FormatUtility.numberFromNumberWithSeparators(val);
                            if (value != null) {
                                return this.checkMinNumber(value, 30);
                            }
                            return false;
                        }),
                })
                .label("Rata"),
            boxOneDurata: Yup.number()
                .when("$rataDurataCollapse", {
                    is: (rataDurataCollapse) => {
                        return rataDurataCollapse === true;
                    },
                    then: Yup.number()
                        .required("Indicare la durata desiderata")
                        .test("min", "La durata richiesta deve essere maggiore di 24 mesi", (val) => {
                            if (val != null) {
                                return this.checkMinNumber(val, 24);
                            }
                            return false;
                        })
                        .test("max", "La durata richiesta deve essere minore di 120 mesi", (val) => {
                            if (val !== undefined) {
                                return this.checkMaxNumber(val, 120);
                            }
                            return false;
                        }),
                })
                .label("Durata"),
            boxTwoImporto: Yup.string()
                .when("$importoRataCollapse", {
                    is: (importoRataCollapse) => {
                        return importoRataCollapse === true;
                    },
                    then: Yup.string()
                        .required("Indicare l'importo desiderato")
                        .notOneOf(["", "0", "0,00"], "Indicare l'importo desiderato")
                        .test("format", "Il formato dell'importo non è valido", (val) => {
                            return this.checkIfImportoIsValid(val);
                        })
                        .test("min", "L'importo richiesto deve essere maggiore di € 1.000,00", (val) => {
                            let value = FormatUtility.numberFromNumberWithSeparators(val);
                            if (value != null) {
                                return this.checkMinNumber(value, 1000);
                            }
                            return false;
                        })
                        .test("max", "L'importo richiesto deve essere minore di € 125.000,00", (val) => {
                            let value = FormatUtility.numberFromNumberWithSeparators(val);
                            if (value !== undefined) {
                                return this.checkMaxNumber(value, 125000);
                            }
                            return false;
                        }),
                })
                .label("Importo"),
            boxTwoRata: Yup.string()
                .when("$importoRataCollapse", {
                    is: (importoRataCollapse) => {
                        return importoRataCollapse === true;
                    },
                    then: Yup.string()
                        .when("$isTipologiaCQP", {
                            is: (isTipologiaCQP) => {
                                return isTipologiaCQP === true;
                            },
                            then: Yup.string().test({
                                name: "max",
                                exclusive: false,
                                params: {},
                                message: "La rata non può essere superiore ad un quinto della pensione netta mensile",
                                test: function (value) {
                                    let val = FormatUtility.numberFromNumberWithSeparators(value) || 0;
                                    let pensione =
                                        FormatUtility.numberFromNumberWithSeparators(
                                            this.parent.pensioneNettaMensile
                                        ) || 0;
                                    return val <= pensione / 5;
                                },
                            }),
                        })
                        .when("$isTipologiaCQP", {
                            is: (isTipologiaCQP) => {
                                return isTipologiaCQP === undefined || isTipologiaCQP === false;
                            },
                            then: Yup.string().test({
                                name: "max",
                                exclusive: false,
                                params: {},
                                message: "La rata non può essere superiore ad un quinto del netto mensile",
                                test: function (value) {
                                    let val = FormatUtility.numberFromNumberWithSeparators(value) || 0;
                                    let netto =
                                        FormatUtility.numberFromNumberWithSeparators(this.parent.nettoMensile) || 0;
                                    return val <= netto / 5;
                                },
                            }),
                        })
                        .required("Indicare la rata desiderata")
                        .notOneOf(["", "0", "0,00"], "Indicare la rata desiderata")
                        .test("format", "Il formato dell'importo rata non è valido", (val) => {
                            return this.checkIfImportoIsValid(val);
                        })
                        .test("min", "La rata richiesta deve essere maggiore di € 30,00", (val) => {
                            let value = FormatUtility.numberFromNumberWithSeparators(val);
                            if (value != null) {
                                return this.checkMinNumber(value, 30);
                            }
                            return false;
                        }),
                })
                .label("Rata"),
            boxThreeImporto: Yup.string()
                .when("$importoDurataCollapse", {
                    is: (importoDurataCollapse) => {
                        return importoDurataCollapse === true;
                    },
                    then: Yup.string()
                        .required("Indicare l'importo desiderato")
                        .notOneOf(["", "0", "0,00"], "Indicare l'importo desiderato")
                        .test("format", "Il formato dell'importo non è valido", (val) => {
                            return this.checkIfImportoIsValid(val);
                        })
                        .test("min", "L'importo richiesto deve essere maggiore di € 1.000,00", (val) => {
                            let value = FormatUtility.numberFromNumberWithSeparators(val);
                            if (value != null) {
                                return this.checkMinNumber(value, 1000);
                            }
                            return false;
                        })
                        .test("max", "L'importo richiesto deve essere minore di € 125.000,00", (val) => {
                            let value = FormatUtility.numberFromNumberWithSeparators(val);
                            if (value !== undefined) {
                                return this.checkMaxNumber(value, 125000);
                            }
                            return false;
                        }),
                })
                .label("Importo"),
            boxThreeDurata: Yup.number()
                .when("$importoDurataCollapse", {
                    is: (importoDurataCollapse) => {
                        return importoDurataCollapse === true;
                    },
                    then: Yup.number()
                        .required("Indicare la durata desiderata")
                        .test("min", "La durata richiesta deve essere maggiore di 24 mesi", (val) => {
                            if (val != null) {
                                return this.checkMinNumber(val, 24);
                            }
                            return false;
                        })
                        .test("max", "La durata richiesta deve essere minore di 120 mesi", (val) => {
                            if (val !== undefined) {
                                return this.checkMaxNumber(val, 120);
                            }
                            return false;
                        }),
                })
                .label("Durata"),
            dataDecorrenza: Yup.string().required("Campo obbligatorio").label("Data decorrenza"),
        });
    }

    public static getSchemaSalvaPoup(): ValidationSchema<SimulazioneStepTwoFormModel> {
        ValidationUtility.setLocale();
        return Yup.object().shape<Partial<SimulazioneStepTwoFormModel>>({
            nome: Yup.string()
                .required("Inserire il nome della simulazione")
                .max(50, "Max 50 caratteri ammessi")
                .label("nome"),
        });
    }

    public static getSchemaSimulazioneDashboard(): ValidationSchema<DashboardSimulazioneFormModel> {
        ValidationUtility.setLocale();
        return Yup.object().shape<Partial<DashboardSimulazioneFormModel>>({
            cliente: Yup.string().required("Selezionare la tipologia di utenza").label("Cliente"),
            tipoRichiesta: Yup.string().required("Indicare il tipo di richiesta").label("Tipo richiesta"),
            importo: Yup.string()
                .required("Indicare l'importo desiderato")
                .notOneOf(["", "0", "0,00"], "Indicare l'importo desiderato")
                .test("format", "Il formato dell'importo non è valido", (val) => {
                    return this.checkIfImportoIsValid(val);
                })
                .test("min", "L'importo richiesto deve essere maggiore di € 1.000,00", (val) => {
                    let value = FormatUtility.numberFromNumberWithSeparators(val);
                    if (value != null) {
                        return this.checkMinNumber(value, 1000);
                    }
                    return false;
                })
                .test("max", "L'importo richiesto deve essere minore di € 125.000,00", (val) => {
                    let value = FormatUtility.numberFromNumberWithSeparators(val);
                    if (value !== undefined) {
                        return this.checkMaxNumber(value, 125000);
                    }
                    return false;
                })
                .label("Importo"),
            dataNascita: Yup.string()
                .required("Il campo 'Data di nascita' è obbligatorio")
                .test("format", "Formato data non valido", (val) => {
                    return this.checkIfDateIsValidOrUndefined(val);
                })
                .test("min", "E' necessario avere meno di 86 anni", (val) => {
                    let minDate = new Date();
                    minDate.setFullYear(minDate.getFullYear() - 86);
                    return this.checkMinDate(val, minDate);
                })
                .test("futura", "Data di nascita futura non ammessa", (val) => {
                    let maxDate = new Date();
                    return this.checkMaxDate(val, maxDate);
                })
                .test("minorenne", "E' necessario essere maggiorenni", (val) => {
                    let maxDate = new Date();
                    maxDate.setFullYear(maxDate.getFullYear() - 18);
                    return this.checkMaxDate(val, maxDate);
                })
                .label("Data nascita"),
            importoMensile: Yup.string()
                .required("Indicare l'importo netto/pensione mensile")
                .notOneOf(["", "0", "0,00"], "Indicare l'importo dello stipendio netto mensile")
                .test("format", "Il formato dell'importo dello stipendio netto mensile non è valido", (val) => {
                    return this.checkIfImportoIsValid(val);
                })
                .test(
                    "min",
                    "L'importo dello stipendio/pensione netto mensile deve essere maggiore di € 563,00",
                    (val) => {
                        let value = FormatUtility.numberFromNumberWithSeparators(val);
                        if (value != null) {
                            return this.checkMinNumber(value, 563);
                        }
                        return false;
                    }
                )
                .test(
                    "max",
                    "L'importo dello stipendio/pensione netto mensile deve essere minore di € 100.000,00",
                    (val) => {
                        let value = FormatUtility.numberFromNumberWithSeparators(val);
                        if (value !== undefined) {
                            return this.checkMaxNumber(value, 100000);
                        }
                        return false;
                    }
                )
                .label("Netto mensile"),
        });
    }

    public static getSchemaRichiestaCedibile(): ValidationSchema<RichiestaCedibileModel> {
        ValidationUtility.setLocale();
        return Yup.object().shape<Partial<RichiestaCedibileModel>>({
            tipoDocumento: Yup.string().required("Campo obbligatorio").label("Tipo Documento"),
            ente: Yup.string().required("Campo obbligatorio").label("Ente"),
            codiceSede: Yup.number()
                .required("Campo obbligatorio")
                .test("len", "Inserire 4 numeri", (val) => {
                    if (val !== undefined || val != null) {
                        return true;
                    }
                    return false;
                })
                .label("Codice Sede"),
            codiceCategoriaPensione: Yup.string()
                .required("Campo obbligatorio")
                .test("len", "Inserire 3 numeri", (val) => {
                    if (val !== undefined || val != null) {
                        return val.toString().length === 3;
                    }
                    return false;
                })
                .label("Codice Categoria Pensione"),
            noCertificatoPensione: Yup.string()
                .required("Campo obbligatorio")
                .test("len", "Inserire 8 numeri", (val) => {
                    if (val !== undefined || val != null) {
                        return val.toString().length === 8;
                    }
                    return false;
                })
                .label("No Certificato Pensione"),
            noDocumento: Yup.string().required("Campo obbligatorio").label("No Documento"),
            codiceFiscale: Yup.string()
                .required("Campo obbligatorio")
                .matches(codiceFiscaleRegex, "Codice fiscale non valido")
                .label("Codice Fiscale"),
            privacyInps: Yup.string().required("Campo obbligatorio").label("Privacy INPS"),
        });
    }

    public static getSchemaCreditoNet(): ValidationSchema<SimulazioneCreditoNetFormModel> {
        ValidationUtility.setLocale();
        return Yup.object().shape<Partial<SimulazioneCreditoNetFormModel>>({
            date: Yup.string()
                .required("Campo obbligatorio")
                .test("format", "La data di ricezione deve essere successiva alla data corrente", (val) => {
                    return this.checkIfDateIsValidOrUndefined(val);
                })
                .test("min", "La data di ricezione deve essere successiva alla data corrente", (val) => {
                    let minDate = new Date();
                    return this.checkMinDate(val, minDate);
                })
                .label("Data Ricezione"),
            taxCode: Yup.string()
                .required("Campo obbligatorio")
                .matches(codiceFiscaleRegex, "Codice fiscale non valido")
                .label("Codice Fiscale"),
            monthSalary: Yup.string().required("Campo obbligatorio").label("Mensilita"),
            idSalary: Yup.string()
                .required("Campo obbligatorio")
                .test("len", "Inserire 8 caratteri", (val) => {
                    if (val !== undefined || val != null) {
                        return val.length === 8;
                    }
                    return false;
                })
                .label("ID Stipendio"),
            agencyCode: Yup.string()
                .required("Campo obbligatorio")
                .test("max", "Codice agenzia non valido", (val) => {
                    if (val !== undefined || val != null) {
                        return val.length <= 20;
                    }
                    return false;
                })
                .label("Cod. Agenzia"),
        });
    }

    public static getSchemaSimulazioneStepTwo(): ValidationSchema<SimulazioneStepTwoFormModel> {
        ValidationUtility.setLocale();
        return Yup.object().shape<Partial<SimulazioneStepTwoFormModel>>({
            // convenzione: Yup.string().required("Campo obbligatorio").label("convenzione"),
        });
    }

    public static getSchemaProvvigioniCommissioniPopup(
        standardBankCommissions: ValoriModel,
        standardAgentCommissions: ValoriModel
    ): ValidationSchema<ProvvigioniComissioniFormModel> {
        ValidationUtility.setLocale();
        return Yup.object().shape<Partial<ProvvigioniComissioniFormModel>>({
            bankCommissionsPercentage: Yup.string()
                .required("Indicare la percentuale desiderata")
                .test("format", "Il formato dell'importo non è valido", (val) => {
                    return this.checkIfImportoIsValid(val);
                })
                // .test("max", "La percentuale deve essere <= 100,00", (val) => {
                //     let value = FormatUtility.numberFromNumberWithSeparators(val);
                //     if (value !== undefined) {
                //         return this.checkMaxNumber(value, 100);
                //     }
                //     return false;
                // })
                // .test({
                //     name: "max",
                //     exclusive: false,
                //     params: {},
                //     message: "La sommatoria delle percentuali deve essere = 100,00",
                //     test: function (value) {
                //         let val = FormatUtility.numberFromNumberWithSeparators(value) || 0;
                //         let percentualeAgente =
                //             FormatUtility.numberFromNumberWithSeparators(this.parent.agentCommissionsPercentage) || 0;
                //         return val + percentualeAgente <= 100;
                //     },
                // })
                .test({
                    name: "max",
                    exclusive: false,
                    params: {},
                    test: function (value) {
                        let val = FormatUtility.numberFromNumberWithSeparators(value) || 0;
                        let max = standardBankCommissions.percentage || 0;
                        return val > max
                            ? this.createError({
                                  message: `La percentuale non può superare la percentuale massimale commissioni di ${max} %.`,
                                  path: "bankCommissionsPercentage",
                              })
                            : true;
                    },
                })
                .test({
                    name: "max",
                    exclusive: false,
                    params: {},
                    test: function (value) {
                        let val = FormatUtility.numberFromNumberWithSeparators(value) || 0;
                        let max = this.parent.maxTotalPercentage || 0;
                        let percentualeAgente =
                            FormatUtility.numberFromNumberWithSeparators(this.parent.agentCommissionsPercentage) || 0;
                        return val + percentualeAgente > max
                            ? this.createError({
                                  message: `La sommatoria delle percentuali non può superare la percentuale massimale commissioni di ${max} %.`,
                                  path: "bankCommissionsPercentage",
                              })
                            : true;
                    },
                })
                .label("Percentuale Commissioni Banka"),
            bankCommissionsAmount: Yup.string()
                .required("Indicare l'importo desiderato")
                .test("format", "Il formato dell'importo non è valido", (val) => {
                    return this.checkIfImportoIsValid(val);
                })
                .test({
                    name: "max",
                    exclusive: false,
                    params: {},
                    test: function (value) {
                        let val = FormatUtility.numberFromNumberWithSeparators(value) || 0;
                        let max = standardBankCommissions.amount || 0;
                        return val > max
                            ? this.createError({
                                  message: `L'importo non può superare l'importo massimale commissioni di € ${max}.`,
                                  path: "bankCommissionsAmount",
                              })
                            : true;
                    },
                })
                .test({
                    name: "max",
                    exclusive: false,
                    params: {},
                    test: function (value) {
                        let val = FormatUtility.numberFromNumberWithSeparators(value) || 0;
                        let max = standardAgentCommissions.amount || 0;
                        let amountAgente =
                            FormatUtility.numberFromNumberWithSeparators(this.parent.agentCommissionsAmount) || 0;
                        return val + amountAgente > max
                            ? this.createError({
                                  message: `La sommatoria di importi non può superare l'importo massimale commissioni di € ${max}.`,
                                  path: "bankCommissionsAmount",
                              })
                            : true;
                    },
                })
                // .test({
                //     name: "min",
                //     exclusive: false,
                //     params: {},
                //     test: function (value) {
                //         let val = FormatUtility.numberFromNumberWithSeparators(value) || 0;
                //         let max = this.parent.maxTotalAmount || 0;
                //         let amountAgente =
                //             FormatUtility.numberFromNumberWithSeparators(this.parent.agentCommissionsAmount) || 0;
                //         return val + amountAgente < max
                //             ? this.createError({
                //                   message: `La sommatoria di importi deve essere = € ${max}.`,
                //                   path: "bankCommissionsAmount",
                //               })
                //             : true;
                //     },
                // })
                .label("Importo Commissioni Banka"),
            agentCommissionsPercentage: Yup.string()
                .required("Indicare la percentuale desiderata")
                .test("format", "Il formato dell'importo non è valido", (val) => {
                    return this.checkIfImportoIsValid(val);
                })
                // .test("max", "La percentuale deve essere <= 100,00", (val) => {
                //     let value = FormatUtility.numberFromNumberWithSeparators(val);
                //     if (value !== undefined) {
                //         return this.checkMaxNumber(value, 100);
                //     }
                //     return false;
                // })
                // .test({
                //     name: "max",
                //     exclusive: false,
                //     params: {},
                //     message: "La sommatoria delle percentuali deve essere = 100,00",
                //     test: function (value) {
                //         let val = FormatUtility.numberFromNumberWithSeparators(value) || 0;
                //         let percentualeBanca =
                //             FormatUtility.numberFromNumberWithSeparators(this.parent.bankCommissionsPercentage) || 0;
                //         return val + percentualeBanca === 100;
                //     },
                // })
                .test({
                    name: "max",
                    exclusive: false,
                    params: {},
                    test: function (value) {
                        let val = FormatUtility.numberFromNumberWithSeparators(value) || 0;
                        let max = standardAgentCommissions.percentage || 0;
                        return val > max
                            ? this.createError({
                                  message: `La percentuale non può superare la percentuale massimale commissioni di ${max} %.`,
                                  path: "agentCommissionsPercentage",
                              })
                            : true;
                    },
                })
                .test({
                    name: "max",
                    exclusive: false,
                    params: {},
                    test: function (value) {
                        let val = FormatUtility.numberFromNumberWithSeparators(value) || 0;
                        let max = this.parent.maxTotalPercentage || 0;
                        let percentualeBanca =
                            FormatUtility.numberFromNumberWithSeparators(this.parent.bankCommissionsPercentage) || 0;
                        return val + percentualeBanca > max
                            ? this.createError({
                                  message: `La sommatoria delle percentuali non può superare la percentuale massimale commissioni di ${max} %.`,
                                  path: "agentCommissionsPercentage",
                              })
                            : true;
                    },
                })
                .label("Percentuale Commissioni Agente"),
            agentCommissionsAmount: Yup.string()
                .required("Indicare l'importo desiderato")
                .test("format", "Il formato dell'importo non è valido", (val) => {
                    return this.checkIfImportoIsValid(val);
                })
                .test({
                    name: "max",
                    exclusive: false,
                    params: {},
                    test: function (value) {
                        let val = FormatUtility.numberFromNumberWithSeparators(value) || 0;
                        let max = standardAgentCommissions.amount || 0;
                        return val > max
                            ? this.createError({
                                  message: `L'importo non può superare l'importo massimale commissioni di € ${max}.`,
                                  path: "agentCommissionsAmount",
                              })
                            : true;
                    },
                })
                .test({
                    name: "max",
                    exclusive: false,
                    params: {},
                    test: function (value) {
                        let val = FormatUtility.numberFromNumberWithSeparators(value) || 0;
                        let max = standardAgentCommissions.amount || 0;
                        let amountBanca =
                            FormatUtility.numberFromNumberWithSeparators(this.parent.bankCommissionsAmount) || 0;
                        return val + amountBanca > max
                            ? this.createError({
                                  message: `La sommatoria di importi non può superare l'importo massimale commissioni di € ${max}.`,
                                  path: "agentCommissionsAmount",
                              })
                            : true;
                    },
                })
                // .test({
                //     name: "min",
                //     exclusive: false,
                //     params: {},
                //     test: function (value) {
                //         let val = FormatUtility.numberFromNumberWithSeparators(value) || 0;
                //         let max = this.parent.maxTotalAmount || 0;
                //         let amountBanca =
                //             FormatUtility.numberFromNumberWithSeparators(this.parent.bankCommissionsAmount) || 0;
                //         return val + amountBanca < max
                //             ? this.createError({
                //                   message: `La sommatoria di importi deve essere = € ${max}.`,
                //                   path: "agentCommissionsAmount",
                //               })
                //             : true;
                //     },
                // })
                .label("Importo Commissioni Agente"),
            maxTotalAmount: Yup.number(),
            maxTotalPercentage: Yup.number(),
        });
    }

    public static getSchemaTanBasePopup(): ValidationSchema<TanBaseFormModel> {
        ValidationUtility.setLocale();
        return Yup.object().shape<Partial<TanBaseFormModel>>({
            tanBase: Yup.string()
                .required("Indicare l'importo desiderato")
                .test("format", "Il formato dell'importo non è valido", (val) => {
                    return this.checkIfImportoIsValid(val);
                })
                .test("max", "La percentuale deve essere <= 100,00", (val) => {
                    let value = FormatUtility.numberFromNumberWithSeparators(val);
                    if (value !== undefined) {
                        return this.checkMaxNumber(value, 100);
                    }
                    return false;
                })
                .label("Tasso su montante"),
        });
    }

    public static getSchemaOneriPopup(): ValidationSchema<OneriFormModel> {
        ValidationUtility.setLocale();
        return Yup.object().shape<Partial<OneriFormModel>>({
            oneri: Yup.string()
                .required("Indicare l'importo desiderato")
                .test("format", "Il formato dell'importo non è valido", (val) => {
                    return this.checkIfImportoIsValid(val);
                })
                .label("Oneri"),
        });
    }

    public static getSchemaSpeseIstruttoriaPopup(): ValidationSchema<SpeseIstruttoriaFormModel> {
        ValidationUtility.setLocale();
        return Yup.object().shape<Partial<SpeseIstruttoriaFormModel>>({
            preliminaryCostsPercentage: Yup.string()
                .required("Indicare la percentuale desiderata")
                .test("format", "Il formato dell'importo non è valido", (val) => {
                    return this.checkIfImportoIsValid(val);
                })
                .test("max", "La percentuale deve essere <= 100,00", (val) => {
                    let value = FormatUtility.numberFromNumberWithSeparators(val);
                    if (value !== undefined) {
                        return this.checkMaxNumber(value, 100);
                    }
                    return false;
                })
                .label("Percentuale Spese Istruttoria"),
            preliminaryCostsAmount: Yup.string()
                .required("Indicare l'importo desiderato")
                .test("format", "Il formato dell'importo non è valido", (val) => {
                    return this.checkIfImportoIsValid(val);
                })
                .test({
                    name: "max",
                    exclusive: false,
                    params: {},
                    test: function (value) {
                        let val = FormatUtility.numberFromNumberWithSeparators(value) || 0;
                        let max = this.parent.maxValue || 0;
                        return val > max
                            ? this.createError({
                                  message: `Le Spese Istruttoria applicate non possono superare l'importo di € ${max}.`,
                                  path: "preliminaryCostsAmount",
                              })
                            : true;
                    },
                })
                .test({
                    name: "min",
                    exclusive: false,
                    params: {},
                    test: function (value) {
                        let val = FormatUtility.numberFromNumberWithSeparators(value) || 0;
                        let min = this.parent.minValue || 0;
                        return val < min
                            ? this.createError({
                                  message: `Le Spese Istruttoria applicate non possono essere inferiori l'importo di € ${min}.`,
                                  path: "preliminaryCostsAmount",
                              })
                            : true;
                    },
                })
                .label("Importo Spese Istruttoria"),
            minValue: Yup.number(),
            maxValue: Yup.number(),
        });
    }

    public static getSchemaCommissioniBancaSegnalantePopup(): ValidationSchema<CommissioniBancaSegnalanteFormModel> {
        ValidationUtility.setLocale();
        return Yup.object().shape<Partial<CommissioniBancaSegnalanteFormModel>>({
            reportingBankCommissionsPercentage: Yup.string()
                .required("Indicare la percentuale desiderata")
                .test("format", "Il formato della percentuale non è valido", (val) => {
                    return this.checkIfImportoIsValid(val);
                })
                .test("max", "La percentuale deve essere <= 100,00", (val) => {
                    let value = FormatUtility.numberFromNumberWithSeparators(val);
                    if (value !== undefined) {
                        return this.checkMaxNumber(value, 100);
                    }
                    return false;
                })
                .label("Percentuale Commissioni Banca Segnalante"),
            reportingBankCommissionsAmount: Yup.string()
                .required("Indicare l'importo desiderato")
                .test("format", "Il formato dell'importo non è valido", (val) => {
                    return this.checkIfImportoIsValid(val);
                })
                .label("Importo Commissioni Banca Segnalante"),
        });
    }

    public static getSchemaSimulazioneStepThree(): ValidationSchema<SimulazioneStepThreeFormModel> {
        ValidationUtility.setLocale();
        return Yup.object().shape<Partial<SimulazioneStepThreeFormModel>>({
            name: Yup.string()
                .test("len", "Il massimo deve essere 50 caratteri", (val) => {
                    if (val !== undefined || val != null) {
                        return val.length <= 50;
                    }
                    return true;
                })
                .when("$setEsitamateDataMandatory", {
                    is: (setEsitamateDataMandatory) => {
                        return setEsitamateDataMandatory === true;
                    },
                    then: Yup.string().required(`Il campo "Nome" è obbligatorio`),
                })
                .label("Nome"),
            surname: Yup.string()
                .test("len", "Il massimo deve essere 50 caratteri", (val) => {
                    if (val !== undefined || val != null) {
                        return val.length <= 50;
                    }
                    return true;
                })
                .when("$setEsitamateDataMandatory", {
                    is: (setEsitamateDataMandatory) => {
                        return setEsitamateDataMandatory === true;
                    },
                    then: Yup.string().required(`Il campo "Cognome" è obbligatorio`),
                })
                .label("Cognome"),
            sex: Yup.string().required(`Il campo "Sesso" è obbligatorio`).label("Sesso"),
            birthNation: Yup.string().label("Nazione di nascita"),
            birthPlace: Yup.string().label("Comune di nascita"),
            birthForeignPlace: Yup.string().label("Comune di nascita"),
            birthDate: Yup.string(),
            residencePlace: Yup.string(),
            taxCode: Yup.string()
                .matches(codiceFiscaleRegex, "Codice fiscale non valido")
                .test({
                    name: "min",
                    exclusive: false,
                    params: {},
                    message: "Codice fiscale non valido",
                    test: function (value) {
                        if (value !== undefined || value != null) {
                            const cognome = this.parent.surname;
                            const nome = this.parent.name;
                            const dataNascita = FormatUtility.dateOrUndefined(this.parent.birthDate);
                            const sesso = this.parent.sex;

                            let luogoNascita = "";
                            const birthNation = this.parent.birthNation;
                            if (birthNation === "Z000") {
                                //If birth nation is Italy, get luogoNascita from communa di nascita(birthPlace)
                                luogoNascita = this.parent.birthPlace;
                            } else {
                                //If birth nation is not Italy, get luogoNascita from nazione di nascita (birthNation)
                                luogoNascita = this.parent.birthNation;
                            }

                            const codiceFiscale = value;
                            const codiceFiscaleCalcolato = FormatUtility.getCodiceFiscale(
                                cognome,
                                nome,
                                dataNascita!,
                                sesso,
                                luogoNascita
                            );

                            if (codiceFiscale && codiceFiscaleCalcolato) {
                                return codiceFiscale.toUpperCase() === codiceFiscaleCalcolato.toUpperCase();
                            }
                        }
                        return true;
                    },
                })
                .label("Codice Fiscale"),
            residenceAddress: Yup.string()
                .test("len", "Il massimo deve essere 25 caratteri", (val) => {
                    if (val !== undefined || val != null) {
                        return val.length <= 25;
                    }
                    return true;
                })
                .label("Indirizzo di residenza"),
            residenceCivicNumber: Yup.string()
                .test("len", "Il massimo deve essere 25 caratteri", (val) => {
                    if (val !== undefined || val != null) {
                        return val.length <= 25;
                    }
                    return true;
                })
                .label("Numero civico"),
            mobileNumber: Yup.string()
                .test("len", "Cellulare non valido", (val) => {
                    if (val !== undefined || val != null) {
                        return val.match(phoneRegex);
                    }
                    return true;
                })
                .test("len", "Il massimo deve essere 15 caratteri", (val) => {
                    if (val !== undefined || val != null) {
                        return val.length <= 15;
                    }
                    return true;
                })
                // .test({
                //     name: "required",
                //     exclusive: false,
                //     params: {},
                //     message: "Il campo `Cellulare` è obbligatorio",
                //     test: function (value) {
                //         if (this.parent.idEstimate !== undefined && this.parent.idEstimate != null) {
                //             return value !== undefined || value != null;
                //         }
                //         return true;
                //     },
                // })
                .when("$setEsitamateDataMandatory", {
                    is: (setEsitamateDataMandatory) => {
                        return setEsitamateDataMandatory === true;
                    },
                    then: Yup.string().required(`Il campo "Cellulare" è obbligatorio`),
                })
                .label("Cellulare"),
            email: Yup.string()
                .when("$setMailMandatory", {
                    is: (setMailMandatory) => {
                        return setMailMandatory === true;
                    },
                    then: Yup.string().required(`Il campo "Mail" è obbligatorio`),
                })
                .test("len", "Indirizzo mail non valido", (val) => {
                    if (val !== undefined || val != null) {
                        return val.match(mailRegex);
                    }
                    return true;
                })
                .label("Mail"),
            thirdPartiesYearExtinctions: Yup.string().required(`Campo obbligatorio`).label("Estinzioni"),
            residenceAddressType: Yup.string().label("DUG"),
            sourceDetail: Yup.string().required(`Campo obbligatorio`).label("Dettaglio Fonte"),
        });
    }

    public static getSchemaNuovoAppuntamento(): ValidationSchema<NuovoAppuntamentoFormModel> {
        ValidationUtility.setLocale();
        return Yup.object().shape<Partial<NuovoAppuntamentoFormModel>>({
            oggetto: Yup.string()
                .required("Campo obbligatorio")
                .test("len", "Il massimo deve essere 50 caratteri", (val) => {
                    if (val !== undefined || val != null) {
                        return val.length <= 50;
                    }
                    return false;
                })
                .label("Oggetto"),
            luogo: Yup.string()
                .required("Campo obbligatorio")
                .test("len", "Il massimo deve essere 50 caratteri", (val) => {
                    if (val !== undefined || val != null) {
                        return val.length <= 50;
                    }
                    return false;
                })
                .label("Luogo"),
            data: Yup.string()
                .required("Campo obbligatorio")
                .test("min", "Data non ammessa", (val) => {
                    let minDate = new Date();
                    return this.checkMinDate(val, minDate);
                })
                .label("Data"),
            oraInizio: Yup.string()
                .required("Campo obbligatorio")
                .test({
                    name: "min",
                    exclusive: false,
                    params: {},
                    message: "Ora non ammessa",
                    test: function (value) {
                        if (value !== undefined || value != null) {
                            let todayTime = moment(new Date()).format("HH:mm");

                            let todayDate = moment(new Date()).format("YYYY-MM-DD");

                            let parent = moment(this.parent.data, "DD/MM/YYYY").format("YYYY-MM-DD");

                            if (parent !== undefined || parent !== null) {
                                let checkDate = moment(parent).isAfter(todayDate);
                                if (checkDate) {
                                    return true;
                                } else {
                                    return value > todayTime;
                                }
                            }
                        }
                        return false;
                    },
                })
                .test("min", "Ora non valida", (val) => {
                    return FormatUtility.validateHhMm(val);
                })
                .label("Ora inizio"),
            oraFine: Yup.string()
                .required("Campo obbligatorio")
                .test({
                    name: "max",
                    exclusive: false,
                    params: {},
                    message: "Ora fine deve essere maggiore di ora inizio",
                    test: function (value) {
                        return value > this.parent.oraInizio;
                    },
                })
                .test("min", "Ora non valida", (val) => {
                    return FormatUtility.validateHhMm(val);
                })
                .label("Ora fine"),
            testo: Yup.string()
                .test("max", "Il massimo deve essere 500 caratteri", (val) => {
                    if (val !== undefined || val != null) {
                        return val.length <= 500;
                    } else {
                        return true;
                    }
                })
                .label("Testo"),
        });
    }

    public static getSchemaNuovoMessaggio(): ValidationSchema<NuovoMessaggioFormModel> {
        ValidationUtility.setLocale();
        return Yup.object().shape<Partial<NuovoMessaggioFormModel>>({
            tipologiaMessaggio: Yup.string().required("Campo obbligatorio").label("Tipologia messaggio"),
            a: Yup.string()
                .when("$inviaBanca", {
                    is: (inviaBanca) => {
                        return inviaBanca === false;
                    },
                    then: Yup.string().required("Campo obbligatorio"),
                })
                .email("Indirizzo mail non valido")
                .label("A"),
            cc: Yup.string()
                .when("$inviaBanca", {
                    is: (inviaBanca) => {
                        return inviaBanca === false;
                    },
                    then: Yup.string().required("Campo obbligatorio"),
                })
                .email("Indirizzo mail non valido")
                .label("CC"),
            oggetto: Yup.string().required("Campo obbligatorio").label("Oggetto"),
            dipendente: Yup.string().required("Campo obbligatorio").label("Dipendente"),
            testo: Yup.string().required("Campo obbligatorio").label("Testo"),
        });
    }

    public static getSchemaNote(): ValidationSchema<NuovoNotaFormModel> {
        ValidationUtility.setLocale();
        return Yup.object().shape<Partial<NuovoNotaFormModel>>({
            nota: Yup.string()
                .required("Campo obbligatorio")
                .max(500, "Il massimo deve essere 500 caratteri")
                .label("Nota"),
            followers: Yup.string()
                .when("$notaInterna", {
                    is: (notaInterna) => {
                        return notaInterna === false;
                    },
                    then: Yup.string()
                        .required("Campo obbligatorio")
                        .test("format", "Indirizzo mail non valido", (val) => {
                            if (val !== undefined || val != null) {
                                let splitted = val.split(";");
                                if (splitted.length > 1) {
                                    let mail = "";
                                    splitted.forEach((email: string) => {
                                        mail = email.trim();
                                    });
                                    return mail.match(mailRegex);
                                } else {
                                    return val.match(mailRegex);
                                }
                            }
                            return false;
                        }),
                })
                .label("Followers"),
        });
    }

    public static getSchemaCercaPreventivo(): ValidationSchema<CercaPreventiviFormModel> {
        ValidationUtility.setLocale();
        return Yup.object().shape<Partial<CercaPreventiviFormModel>>({
            dataCreazioneA: Yup.string()
                .test("format", "Formato data non valido", (val) => {
                    return this.checkIfDateIsValidOrUndefined(val);
                })
                .label("Data creazione a"),
            dataCreazioneDa: Yup.string()
                .test("format", "Formato data non valido", (val) => {
                    return this.checkIfDateIsValidOrUndefined(val);
                })
                .label("Data creazione da"),
        });
    }

    public static getSchemaCercaSimulazione(): ValidationSchema<CercaSimulazioneFormModel> {
        ValidationUtility.setLocale();
        return Yup.object().shape<Partial<CercaSimulazioneFormModel>>({
            dataCreazioneA: Yup.string()
                .test("format", "Formato data non valido", (val) => {
                    return this.checkIfDateIsValidOrUndefined(val);
                })
                .label("Data creazione a"),
            dataCreazioneDa: Yup.string()
                .test("format", "Formato data non valido", (val) => {
                    return this.checkIfDateIsValidOrUndefined(val);
                })
                .label("Data creazione da"),
        });
    }
}
